import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import { OptimizedImage } from './OptimizedImage';

interface CardImageCarouselProps {
  mainImage: string;
  images?: string[];
  alt: string;
  className?: string;
}

export const CardImageCarousel: React.FC<CardImageCarouselProps> = ({
  mainImage,
  images,
  alt,
  className = 'w-full h-full object-cover group-hover:scale-105 transition-transform duration-500',
}) => {
  const photoList = useMemo(() => {
    const raw = [mainImage, ...(Array.isArray(images) ? images : [])].filter(
      (url): url is string => typeof url === 'string' && url.trim().length > 0
    );
    return Array.from(new Set(raw));
  }, [mainImage, images]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);

  const total = photoList.length;
  const safeIndex = currentIndex < total ? currentIndex : 0;
  const activeSrc = photoList[safeIndex] || mainImage;

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentIndex((prev) => (prev - 1 + total) % total);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setCurrentIndex((prev) => (prev + 1) % total);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (total <= 1) return;
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (total <= 1 || touchStartX === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(deltaX) > 36) {
      e.stopPropagation();
      if (deltaX < 0) {
        setCurrentIndex((prev) => (prev + 1) % total);
      } else {
        setCurrentIndex((prev) => (prev - 1 + total) % total);
      }
    }
    setTouchStartX(null);
  };

  return (
    <div
      className="relative w-full h-full overflow-hidden select-none"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <OptimizedImage
        src={activeSrc}
        alt={total > 1 ? `${alt} (${safeIndex + 1}/${total})` : alt}
        width={400}
        height={300}
        loading="lazy"
        className={className}
      />

      {total > 1 && (
        <>
          {/* Left / Right Navigation Arrows */}
          <button
            type="button"
            onClick={handlePrev}
            aria-label="Previous photo"
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/90 hover:bg-white text-slate-900 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-200 z-10 cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4 stroke-[2.2]" />
          </button>
          <button
            type="button"
            onClick={handleNext}
            aria-label="Next photo"
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/90 hover:bg-white text-slate-900 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity duration-200 z-10 cursor-pointer"
          >
            <ChevronRight className="w-4 h-4 stroke-[2.2]" />
          </button>

          {/* Subtle Photo Counter Pill (Bottom Left) */}
          <div className="absolute bottom-3 left-3 px-2 py-0.5 rounded-md bg-black/60 backdrop-blur-xs text-white text-[11px] font-semibold flex items-center gap-1 z-10 pointer-events-none">
            <Camera className="w-3 h-3 text-white/90" />
            <span>
              {safeIndex + 1}/{total}
            </span>
          </div>

          {/* Pagination Dots (Bottom Center) */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 z-10 pointer-events-none">
            {photoList.slice(0, 5).map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all ${
                  idx === safeIndex ? 'w-3.5 bg-white shadow-xs' : 'w-1.5 bg-white/60'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
